import { type FormEvent, useEffect, useState } from "react";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { useClientAuth } from "../lib/auth-context";
import { ClientLink, useClientRouter } from "../lib/router-context";

type LinkState = "checking" | "valid" | "invalid" | "error";

function passwordError(password: string): string | null {
  if (password.length < 10) return "Le mot de passe doit contenir au moins 10 caractères.";
  if (!/\d/.test(password)) return "Le mot de passe doit contenir au moins 1 chiffre.";
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]/.test(password)) {
    return "Le mot de passe doit contenir au moins 1 caractère spécial.";
  }
  return null;
}

export default function ActivationPage() {
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [linkState, setLinkState] = useState<LinkState>("checking");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { login } = useClientAuth();
  const { navigate } = useClientRouter();

  useEffect(() => {
    if (!token) {
      setLinkState("invalid");
      return;
    }

    const controller = new AbortController();
    fetch(`/api/espace-client/auth/link?token=${encodeURIComponent(token)}&type=activation`, {
      signal: controller.signal,
      cache: "no-store",
    })
      .then(async response => {
        if (!response.ok) throw new Error("verification-failed");
        const data = await response.json();
        setLinkState(data.valid === true ? "valid" : "invalid");
      })
      .catch((requestError: unknown) => {
        if (requestError instanceof DOMException && requestError.name === "AbortError") return;
        setLinkState("error");
      });

    return () => controller.abort();
  }, [token]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const strengthError = passwordError(password);
    if (strengthError) { setError(strengthError); return; }
    if (password !== confirmation) { setError("Les mots de passe ne correspondent pas."); return; }
    if (!token) { setLinkState("invalid"); return; }

    setSubmitting(true);
    setError(null);
    try {
      const response = await fetch("/api/espace-client/auth/activate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newPassword: password }),
      });
      const data = await response.json();
      if (!response.ok) {
        if ([400, 401, 403, 404, 410].includes(response.status)) {
          setLinkState("invalid");
          return;
        }
        setError("L’activation n’a pas pu aboutir. Veuillez réessayer.");
        return;
      }

      login(data.token, data.user, data.client);
      navigate("/");
    } catch {
      setError("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(135deg, #4e63f0 0%, #2facf3 100%)" }}>
      <div className="w-full max-w-md space-y-6">
        <div className="text-center text-white">
          <h1 className="text-2xl font-bold">Activer mon espace client</h1>
          <p className="text-sm text-white/75 mt-2">Choisissez votre mot de passe personnel.</p>
        </div>

        <div className="bg-card border border-border/50 rounded-2xl shadow-2xl p-6">
          {linkState === "checking" && (
            <p className="flex items-center justify-center gap-2 text-sm text-muted-foreground" role="status">
              <Loader2 className="w-4 h-4 animate-spin" /> Vérification du lien…
            </p>
          )}

          {linkState === "error" && (
            <div className="space-y-4 text-center">
              <p className="text-sm text-destructive" role="alert">Impossible de vérifier le lien pour le moment. Veuillez réessayer.</p>
              <button type="button" onClick={() => window.location.reload()} className="text-sm text-primary hover:underline">
                Réessayer
              </button>
            </div>
          )}

          {linkState === "invalid" && (
            <div className="space-y-4 text-center">
              <div className="flex justify-center"><AlertCircle className="w-8 h-8 text-destructive" /></div>
              <h2 className="font-semibold">Ce lien n’est plus valide</h2>
              <p className="text-sm text-muted-foreground">
                Il a peut-être expiré ou a déjà été utilisé. Pour une première activation, demandez une nouvelle invitation à Jobotto.
              </p>
              <div className="flex flex-col gap-2">
                <a href="mailto:contact@jobotto.fr?subject=Nouvelle%20invitation%20espace%20client" className="text-sm text-primary hover:underline">
                  Contacter Jobotto
                </a>
                <ClientLink to="/mot-de-passe-oublie" className="text-sm text-primary hover:underline">
                  Compte déjà activé ? Mot de passe oublié
                </ClientLink>
              </div>
            </div>
          )}

          {linkState === "valid" && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="activation-password" className="text-sm font-medium">Nouveau mot de passe</label>
                <input
                  id="activation-password"
                  type="password"
                  autoComplete="new-password"
                  minLength={10}
                  required
                  value={password}
                  onChange={event => setPassword(event.target.value)}
                  className="w-full h-11 mt-1.5 px-3 rounded-lg border border-input bg-background text-sm"
                />
              </div>
              <div>
                <label htmlFor="activation-confirmation" className="text-sm font-medium">Confirmer le mot de passe</label>
                <input
                  id="activation-confirmation"
                  type="password"
                  autoComplete="new-password"
                  minLength={10}
                  required
                  value={confirmation}
                  onChange={event => setConfirmation(event.target.value)}
                  className="w-full h-11 mt-1.5 px-3 rounded-lg border border-input bg-background text-sm"
                />
              </div>
              <p className="text-xs text-muted-foreground">10 caractères minimum, avec au moins un chiffre et un caractère spécial.</p>
              {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="w-full h-11 rounded-lg font-medium text-sm flex items-center justify-center gap-2 text-white disabled:opacity-60"
                style={{ background: "linear-gradient(135deg, #4e63f0 0%, #2facf3 100%)" }}
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Activer mon compte
              </button>
            </form>
          )}
        </div>
        <p className="text-center text-xs text-white/70">
          <CheckCircle2 className="inline w-3.5 h-3.5 mr-1" />
          Espace Client Jobotto
        </p>
      </div>
    </div>
  );
}